import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { devLog, devWarn, devError } from '../utils/devLogger';
import { flutterBridge } from '../utils/flutterBridge';

interface NotificationContextType {
  hasUnreadNotification: boolean;
  setHasUnreadNotification: (hasUnread: boolean) => void;
  markNotificationAsRead: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);

  useEffect(() => {
    // Flutter Bridge를 통한 FCM 수신 알림 처리
    const handleFCMReceived = (event: CustomEvent) => {
      devLog('React: FCM 수신 알림 처리', event.detail);
      setHasUnreadNotification(true);
    };

    // Flutter WebView 환경에서만 리스너 등록
    if (flutterBridge.isFlutterWebView()) {
      // Flutter Bridge의 CustomEvent 리스너
      window.addEventListener('flutter-fcm-received', handleFCMReceived as EventListener);
      
      // 직접 메시지 리스너도 등록 (백업용)
      const handleMessage = (event: MessageEvent) => {
        try {
          const message = event.data;
          if (message?.type === 'FCM_NOTIFICATION_RECEIVED') {
            devLog('React: 직접 메시지로 FCM 수신 알림 처리', message.data);
            setHasUnreadNotification(true);
          }
        } catch (error) {
          devError('NotificationContext 메시지 처리 오류:', error);
        }
      };

      window.addEventListener('message', handleMessage);
      
      return () => {
        window.removeEventListener('flutter-fcm-received', handleFCMReceived as EventListener);
        window.removeEventListener('message', handleMessage);
      };
    }
  }, []);

  // 알림을 읽음으로 표시
  const markNotificationAsRead = () => {
    setHasUnreadNotification(false);
    devLog('NotificationContext: 알림을 읽음으로 표시');
  };

  const contextValue: NotificationContextType = {
    hasUnreadNotification,
    setHasUnreadNotification,
    markNotificationAsRead,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};
